import { 
  User, 
  Agent, 
  Workflow, 
  Integration, 
  IntegrationTool, 
  Workspace, 
  AgentExecution, 
  WorkflowExecution, 
  AgentMetrics, 
  SystemMetrics 
} from '@/types';

// Mock Users
export const mockUsers: User[] = [
  {
    id: 1,
    email: 'demo@orchestra.ai',
    username: 'demo',
    full_name: 'Demo User',
    workspace_id: 'ws-12345',
    workspace_name: 'Development Team',
    is_active: true,
    is_superuser: false,
    created_at: '2024-01-01T10:00:00Z',
    updated_at: '2024-01-21T15:30:00Z'
  },
  {
    id: 2,
    email: 'jane.smith@example.com',
    username: 'janesmith',
    full_name: 'Jane Smith',
    workspace_id: 'ws-67890',
    workspace_name: 'QA Team',
    is_active: true,
    is_superuser: true,
    created_at: '2024-01-02T11:00:00Z',
    updated_at: '2024-01-20T14:20:00Z'
  }
];

// Mock Workspaces
export const mockWorkspaces: Workspace[] = [
  {
    id: 'ws-12345',
    name: 'Development Team',
    description: 'Main development workspace for the engineering team',
    settings: {
      default_model: 'gpt-4o',
      max_agents: 50,
      max_workflows: 25,
      allow_external_integrations: true,
      theme: 'dark',
      project_type: 'software_development'
    },
    owner_id: 1,
    is_active: true,
    agent_count: 12,
    workflow_count: 8,
    created_at: '2024-01-01T10:00:00Z',
    updated_at: '2024-01-21T15:30:00Z'
  },
  {
    id: 'ws-67890',
    name: 'QA Team',
    description: 'Quality assurance and testing workspace',
    settings: {
      default_model: 'gpt-4o-mini',
      max_agents: 25,
      max_workflows: 15,
      allow_external_integrations: true,
      theme: 'light',
      project_type: 'quality_assurance'
    },
    owner_id: 2,
    is_active: true,
    agent_count: 8,
    workflow_count: 5,
    created_at: '2024-01-02T11:00:00Z',
    updated_at: '2024-01-20T14:20:00Z'
  }
];

// Mock Agents
export const mockAgents: Agent[] = [
  {
    id: 1,
    name: 'Code Review Assistant',
    description: 'AI agent specialized in reviewing code for quality, security, and best practices',
    instructions: `You are a Code Review Assistant specialized in analyzing GitHub repositories for code quality, security vulnerabilities, and best practices.

## YOUR CAPABILITIES ##
You can analyze repositories using these GitHub tools:
- get_github_repository: Get repository information and metadata
- get_github_repository_content: Browse repository files and directories
- get_github_pull_requests: Review existing pull requests
- get_repository_statistics: Get comprehensive repository metrics

## CONTEXT HANDLING ##
When you receive a task, look for extracted context about:
- **Repository**: GitHub repository name (owner/repo format)
- **Branch**: Specific branch to analyze (defaults to main)
- **Analysis Focus**: What type of review is needed (code quality, security, documentation)
- **Technologies**: Programming languages and frameworks mentioned

## ANALYSIS PROCESS ##

### Step 1: Parse Context
- Extract repository name from the task description or context
- If no repository is specified, ask for clarification: "Please provide the GitHub repository name (in owner/repo format) that you'd like me to review"
- Identify the scope of analysis requested

### Step 2: Repository Overview
- Use get_github_repository to get basic repository information
- Use get_repository_statistics to understand the project scale
- Check the primary programming language and framework

### Step 3: Code Structure Analysis
- Use get_github_repository_content to explore the repository structure
- Analyze folder organization and architecture patterns
- Identify key files (README, package.json, requirements.txt, etc.)

### Step 4: Code Quality Review
Based on the repository content, analyze:
- **Code Organization**: Is the project well-structured?
- **Naming Conventions**: Are files and directories properly named?
- **Documentation**: Does the repository have adequate documentation?
- **Dependencies**: Are dependencies up-to-date and secure?
- **Configuration**: Are configuration files properly structured?

### Step 5: Security Assessment
Look for common security issues:
- **Secrets Management**: Are API keys or passwords exposed?
- **Dependencies**: Are there known vulnerabilities in dependencies?
- **File Permissions**: Are sensitive files properly protected?
- **Input Validation**: Does the code handle user input securely?

### Step 6: Best Practices Check
Evaluate adherence to best practices:
- **Version Control**: Are commits well-structured?
- **Branch Strategy**: Is there a clear branching strategy?
- **Testing**: Is there evidence of testing infrastructure?
- **CI/CD**: Are there automated build/deployment processes?

## OUTPUT FORMAT ##
Provide a comprehensive review with:

**🏗️ Repository Overview**
- Repository name, language, size, last updated
- Primary technologies and frameworks used
- Repository structure and organization

**✅ Strengths**
- List positive aspects of the codebase
- Good practices being followed
- Well-implemented features

**⚠️ Areas for Improvement**
- Code quality issues found
- Security concerns identified
- Documentation gaps
- Structural improvements needed

**🔒 Security Findings**
- Potential security vulnerabilities
- Exposed secrets or sensitive data
- Dependency security issues

**📋 Recommendations**
- Specific actionable improvements
- Priority level for each recommendation
- Implementation guidance

**📊 Summary Metrics**
- Code quality score (if applicable)
- Security assessment level
- Documentation completeness

## CRITICAL RULES ##
- ALWAYS request repository information if not provided
- NEVER make assumptions about repository names
- FOCUS on actionable, specific feedback
- PRIORITIZE security issues as high importance
- PROVIDE clear examples when identifying issues

If you receive a task without repository context, respond: "I'd be happy to review code for you! Please provide the GitHub repository name (in owner/repository format, like 'microsoft/vscode') that you'd like me to analyze."`,
    model: 'gpt-4o',
    tools: ['get_github_repository', 'get_github_repository_content', 'get_github_pull_requests', 'get_repository_statistics'],
    handoffs: [],
    guardrails: { 
      input: { validate_repo_format: true }, 
      output: { include_security_assessment: true } 
    },
    model_settings: { 
      temperature: 0.3, 
      max_tokens: 4000 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-05T09:00:00Z',
    updated_at: '2024-01-20T16:45:00Z',
    status: 'active',
    usage_count: 145,
    avg_response_time: 2340,
    success_rate: 0.94,
    last_used: '2024-01-21T14:30:00Z',
    created_by: 'johndoe'
  },
  {
    id: 2,
    name: 'Documentation Generator',
    description: 'Generates comprehensive documentation from code and specifications',
    instructions: 'Create detailed documentation including API docs, user guides, and technical specifications based on code analysis and requirements.',
    model: 'gpt-4o',
    tools: { 
      tools: ['create_confluence_page', 'update_confluence_page', 'search_code', 'get_repository_info'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_code_context: true }, 
      output: { ensure_completeness: true } 
    },
    model_settings: { 
      temperature: 0.4, 
      max_tokens: 3000 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-08T11:30:00Z',
    updated_at: '2024-01-19T10:15:00Z',
    status: 'active',
    usage_count: 89,
    avg_response_time: 3450,
    success_rate: 0.97,
    last_used: '2024-01-21T11:20:00Z',
    created_by: 'johndoe'
  },
  {
    id: 3,
    name: 'Bug Hunter',
    description: 'Specialized agent for detecting and analyzing software bugs',
    instructions: 'Analyze code for potential bugs, security vulnerabilities, and performance issues. Provide detailed analysis with reproduction steps.',
    model: 'gpt-4o',
    tools: { 
      tools: ['search_code', 'get_repository_commits', 'create_jira_issue', 'get_pull_request_files'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_code_input: true }, 
      output: { include_severity_classification: true } 
    },
    model_settings: { 
      temperature: 0.2, 
      max_tokens: 2500 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-10T14:00:00Z',
    updated_at: '2024-01-21T09:30:00Z',
    status: 'active',
    usage_count: 67,
    avg_response_time: 2890,
    success_rate: 0.91,
    last_used: '2024-01-21T16:45:00Z',
    created_by: 'johndoe'
  },
  {
    id: 4,
    name: 'API Integrator',
    description: 'Handles API integration tasks and endpoint management',
    instructions: 'Manage API integrations, create endpoints, handle authentication, and ensure proper data flow between services.',
    model: 'gpt-4o-mini',
    tools: { 
      tools: ['create_pull_request', 'update_jira_issue', 'search_confluence', 'get_repository_info'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_api_specs: true }, 
      output: { include_testing_steps: true } 
    },
    model_settings: { 
      temperature: 0.3, 
      max_tokens: 2000 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-12T16:20:00Z',
    updated_at: '2024-01-18T13:10:00Z',
    status: 'active',
    usage_count: 34,
    avg_response_time: 1980,
    success_rate: 0.88,
    last_used: '2024-01-20T15:30:00Z',
    created_by: 'johndoe'
  },
  {
    id: 5,
    name: 'Test Generator',
    description: 'Creates comprehensive test suites for applications',
    instructions: 'Generate unit tests, integration tests, and end-to-end tests based on code analysis and requirements.',
    model: 'gpt-4o-mini',
    tools: { 
      tools: ['get_repository_info', 'create_pull_request', 'search_code', 'update_jira_issue'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_code_coverage: true }, 
      output: { ensure_test_completeness: true } 
    },
    model_settings: { 
      temperature: 0.4, 
      max_tokens: 2500 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-14T10:45:00Z',
    updated_at: '2024-01-21T12:20:00Z',
    status: 'active',
    usage_count: 56,
    avg_response_time: 2150,
    success_rate: 0.93,
    last_used: '2024-01-21T13:15:00Z',
    created_by: 'johndoe'
  },
  {
    id: 6,
    name: 'Performance Optimizer',
    description: 'Analyzes and optimizes application performance',
    instructions: 'Analyze code for performance bottlenecks, suggest optimizations, and provide performance improvement recommendations.',
    model: 'gpt-4o',
    tools: { 
      tools: ['search_code', 'get_repository_commits', 'create_confluence_page', 'add_comment_to_issue'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_performance_metrics: true }, 
      output: { include_benchmarks: true } 
    },
    model_settings: { 
      temperature: 0.3, 
      max_tokens: 2200 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: false,
    is_builtin: false,
    agent_type: 'user',
    created_at: '2024-01-16T13:30:00Z',
    updated_at: '2024-01-19T08:45:00Z',
    status: 'inactive',
    usage_count: 23,
    avg_response_time: 3200,
    success_rate: 0.87,
    last_used: '2024-01-19T08:45:00Z',
    created_by: 'johndoe'
  },
  // Built-in agents
  {
    id: 7,
    name: 'SDLC Workflow Specialist',
    description: 'Expert in Software Development Lifecycle workflows and processes',
    instructions: 'Provide guidance on SDLC best practices, workflow optimization, and process improvement.',
    model: 'gpt-4o',
    tools: { 
      tools: ['get_pull_request', 'create_jira_issue', 'transition_issue', 'create_confluence_page'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_infrastructure: true }, 
      output: { include_rollback: true } 
    },
    model_settings: { 
      temperature: 0.3, 
      max_tokens: 2000 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: true,
    agent_type: 'builtin',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    status: 'active',
    usage_count: 0,
    avg_response_time: 0,
    success_rate: 1.0,
    last_used: null,
    created_by: 'system'
  },
  {
    id: 8,
    name: 'Project Management Specialist',
    description: 'Expert in project management workflows using Jira and Confluence',
    instructions: 'Assist with project planning, team coordination, and project management best practices.',
    model: 'gpt-4o',
    tools: { 
      tools: ['get_all_boards', 'get_board_issues', 'create_sprint', 'create_confluence_page'] 
    },
    handoffs: [],
    guardrails: { 
      input: { validate_team_capacity: true }, 
      output: { include_stakeholder_approval: true } 
    },
    model_settings: { 
      temperature: 0.4, 
      max_tokens: 2000 
    },
    user_id: 1,
    workspace_id: 'ws-12345',
    is_active: true,
    is_builtin: true,
    agent_type: 'builtin',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    status: 'active',
    usage_count: 0,
    avg_response_time: 0,
    success_rate: 1.0,
    last_used: null,
    created_by: 'system'
  }
];

// Mock Workflows
export const mockWorkflows: Workflow[] = [
  {
    id: '1',
    name: 'Code Review Pipeline',
    description: 'Automated code review workflow with security scanning and documentation generation',
    agents: ['1', '2', '6'],
    steps: [
      { id: '1', agent_id: '1', order: 1, timeout: 300 },
      { id: '2', agent_id: '6', order: 2, timeout: 600 },
      { id: '3', agent_id: '2', order: 3, timeout: 180 }
    ],
    status: 'active',
    created_by: 'johndoe',
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-20T15:30:00Z',
    execution_count: 45,
    success_rate: 0.93
  },
  {
    id: '2',
    name: 'Bug Detection & Fix',
    description: 'Comprehensive bug hunting and automated fix suggestion workflow',
    agents: ['3', '5', '1'],
    steps: [
      { id: '4', agent_id: '3', order: 1, timeout: 450 },
      { id: '5', agent_id: '5', order: 2, timeout: 300 },
      { id: '6', agent_id: '1', order: 3, timeout: 240 }
    ],
    status: 'active',
    created_by: 'johndoe',
    created_at: '2024-01-12T08:00:00Z',
    updated_at: '2024-01-19T14:20:00Z',
    execution_count: 28,
    success_rate: 0.89
  },
  {
    id: '3',
    name: 'API Integration Setup',
    description: 'End-to-end API integration with testing and documentation',
    agents: ['4', '5', '2'],
    steps: [
      { id: '7', agent_id: '4', order: 1, timeout: 600 },
      { id: '8', agent_id: '5', order: 2, timeout: 300 },
      { id: '9', agent_id: '2', order: 3, timeout: 240 }
    ],
    status: 'draft',
    created_by: 'johndoe',
    created_at: '2024-01-18T16:30:00Z',
    updated_at: '2024-01-21T11:45:00Z',
    execution_count: 12,
    success_rate: 0.75
  }
];

// Mock Integrations
export const mockIntegrations: Integration[] = [
  {
    id: '1',
    name: 'Jira Integration',
    type: 'jira',
    status: 'connected',
    config: {
      url: 'https://company.atlassian.net',
      project_key: 'PROJ',
      username: 'api-user',
      last_sync_count: 145
    },
    created_at: '2024-01-10T09:00:00Z',
    updated_at: '2024-01-21T14:30:00Z',
    last_sync: '2024-01-21T14:30:00Z',
    error_message: null
  },
  {
    id: '2',
    name: 'GitHub Repository',
    type: 'github',
    status: 'connected',
    config: {
      repository: 'company/main-app',
      owner: 'company',
      branch: 'main',
      webhook_enabled: true,
      last_commit: 'abc123def'
    },
    created_at: '2024-01-12T11:20:00Z',
    updated_at: '2024-01-21T16:45:00Z',
    last_sync: '2024-01-21T16:45:00Z',
    error_message: null
  },
  {
    id: '3',
    name: 'Confluence Wiki',
    type: 'confluence',
    status: 'connected',
    config: {
      url: 'https://company.atlassian.net/wiki',
      space_key: 'DEV',
      last_page_count: 87
    },
    created_at: '2024-01-15T16:30:00Z',
    updated_at: '2024-01-20T10:15:00Z',
    last_sync: '2024-01-20T10:15:00Z',
    error_message: null
  },
  {
    id: '4',
    name: 'Slack Workspace',
    type: 'slack',
    status: 'error',
    config: {
      workspace: 'company-team',
      channel: '#dev-alerts',
      bot_token: 'xoxb-***'
    },
    created_at: '2024-01-18T14:00:00Z',
    updated_at: '2024-01-21T08:30:00Z',
    last_sync: '2024-01-19T12:00:00Z',
    error_message: 'Authentication failed: Token expired'
  }
];

// Mock Integration Tools
export const mockIntegrationTools: IntegrationTool[] = [
  // Jira Tools
  {
    name: 'create_jira_issue',
    description: 'Create a new Jira issue',
    parameters: {
      project_key: { type: 'string', required: true, description: 'Project key' },
      summary: { type: 'string', required: true, description: 'Issue summary' },
      description: { type: 'string', required: false, description: 'Issue description' },
      issue_type: { type: 'string', required: false, description: 'Issue type (Bug, Task, Story)' }
    },
    integration_type: 'jira'
  },
  {
    name: 'update_jira_issue',
    description: 'Update an existing Jira issue',
    parameters: {
      issue_key: { type: 'string', required: true, description: 'Issue key (e.g., PROJ-123)' },
      summary: { type: 'string', required: false, description: 'New summary' },
      description: { type: 'string', required: false, description: 'New description' },
      status: { type: 'string', required: false, description: 'New status' }
    },
    integration_type: 'jira'
  },
  {
    name: 'get_jira_issue',
    description: 'Get details of a Jira issue',
    parameters: {
      issue_key: { type: 'string', required: true, description: 'Issue key (e.g., PROJ-123)' }
    },
    integration_type: 'jira'
  },
  {
    name: 'transition_issue',
    description: 'Transition a Jira issue to a different status',
    parameters: {
      issue_key: { type: 'string', required: true, description: 'Issue key' },
      transition_id: { type: 'string', required: true, description: 'Transition ID' }
    },
    integration_type: 'jira'
  },
  
  // GitHub Tools
  {
    name: 'get_pull_request',
    description: 'Get details of a GitHub pull request',
    parameters: {
      owner: { type: 'string', required: true, description: 'Repository owner' },
      repo: { type: 'string', required: true, description: 'Repository name' },
      pull_number: { type: 'number', required: true, description: 'Pull request number' }
    },
    integration_type: 'github'
  },
  {
    name: 'get_pull_request_files',
    description: 'Get files changed in a pull request',
    parameters: {
      owner: { type: 'string', required: true, description: 'Repository owner' },
      repo: { type: 'string', required: true, description: 'Repository name' },
      pull_number: { type: 'number', required: true, description: 'Pull request number' }
    },
    integration_type: 'github'
  },
  {
    name: 'create_pull_request',
    description: 'Create a new pull request',
    parameters: {
      owner: { type: 'string', required: true, description: 'Repository owner' },
      repo: { type: 'string', required: true, description: 'Repository name' },
      title: { type: 'string', required: true, description: 'Pull request title' },
      body: { type: 'string', required: false, description: 'Pull request description' },
      head: { type: 'string', required: true, description: 'Head branch' },
      base: { type: 'string', required: true, description: 'Base branch' }
    },
    integration_type: 'github'
  },
  {
    name: 'search_code',
    description: 'Search for code in repository',
    parameters: {
      query: { type: 'string', required: true, description: 'Search query' },
      owner: { type: 'string', required: true, description: 'Repository owner' },
      repo: { type: 'string', required: true, description: 'Repository name' }
    },
    integration_type: 'github'
  },
  
  // Confluence Tools
  {
    name: 'create_confluence_page',
    description: 'Create a new Confluence page',
    parameters: {
      space_key: { type: 'string', required: true, description: 'Space key' },
      title: { type: 'string', required: true, description: 'Page title' },
      content: { type: 'string', required: true, description: 'Page content (HTML)' },
      parent_id: { type: 'string', required: false, description: 'Parent page ID' }
    },
    integration_type: 'confluence'
  },
  {
    name: 'update_confluence_page',
    description: 'Update an existing Confluence page',
    parameters: {
      page_id: { type: 'string', required: true, description: 'Page ID' },
      title: { type: 'string', required: false, description: 'New page title' },
      content: { type: 'string', required: false, description: 'New page content (HTML)' }
    },
    integration_type: 'confluence'
  },
  {
    name: 'search_confluence',
    description: 'Search for Confluence pages',
    parameters: {
      query: { type: 'string', required: true, description: 'Search query' },
      space: { type: 'string', required: false, description: 'Space key to search in' }
    },
    integration_type: 'confluence'
  }
];

// Mock Agent Executions
export const mockAgentExecutions: AgentExecution[] = [
  {
    id: 'exec-1',
    agent_id: '1',
    input: 'Review the latest pull request for security issues',
    output: 'Found 2 potential security vulnerabilities: SQL injection risk in user input validation and XSS vulnerability in comment rendering. Recommended fixes provided.',
    status: 'completed',
    started_at: '2024-01-21T14:30:00Z',
    completed_at: '2024-01-21T14:32:15Z',
    execution_time: 135000,
    error_message: null
  },
  {
    id: 'exec-2',
    agent_id: '2',
    input: 'Generate documentation for the new API endpoints',
    output: 'Created comprehensive API documentation with examples, error codes, and usage guidelines.',
    status: 'completed',
    started_at: '2024-01-21T11:20:00Z',
    completed_at: '2024-01-21T11:23:45Z',
    execution_time: 225000,
    error_message: null
  }
];

// Mock System Metrics
export const mockSystemMetrics: SystemMetrics = {
  total_agents: 8,
  active_agents: 7,
  total_workflows: 3,
  active_workflows: 2,
  total_executions_today: 23,
  successful_executions_today: 21,
  avg_response_time_today: 2450,
  total_cost_today: 12.45
};

// Mock Agent Metrics
export const mockAgentMetrics: AgentMetrics[] = [
  {
    agent_id: '1',
    agent_name: 'Code Review Assistant',
    total_executions: 145,
    successful_executions: 136,
    failed_executions: 9,
    avg_response_time: 2340,
    total_tokens_used: 45680,
    cost_usd: 8.23,
    last_execution: '2024-01-21T14:30:00Z'
  },
  {
    agent_id: '2',
    agent_name: 'Documentation Generator',
    total_executions: 89,
    successful_executions: 86,
    failed_executions: 3,
    avg_response_time: 3450,
    total_tokens_used: 67890,
    cost_usd: 12.15,
    last_execution: '2024-01-21T11:20:00Z'
  },
  {
    agent_id: '3',
    agent_name: 'Bug Hunter',
    total_executions: 67,
    successful_executions: 61,
    failed_executions: 6,
    avg_response_time: 2890,
    total_tokens_used: 34567,
    cost_usd: 6.78,
    last_execution: '2024-01-21T16:45:00Z'
  }
];

// Utility function to simulate API delay
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Mock API response wrapper
export const mockApiResponse = async <T>(data: T, delayMs: number = 300): Promise<T> => {
  await delay(delayMs);
  return data;
};

// Pagination utility
export const paginate = <T>(items: T[], page: number, size: number) => {
  const start = (page - 1) * size;
  const end = start + size;
  return {
    items: items.slice(start, end),
    total: items.length,
    page,
    size,
    pages: Math.ceil(items.length / size)
  };
}; 